import React, { useState, useEffect, useCallback } from 'react';
import socketService from '../services/socketService'; 
import Layout from '../components/Layout'; 

const groupNumbersByBank = (allNumbers) => {
    const numbersByBank = {};
    allNumbers.forEach(n => {
        if (!numbersByBank[n.bank_id]) {
            numbersByBank[n.bank_id] = [];
        }
        numbersByBank[n.bank_id].push(n);
    });
    return numbersByBank;
};

const MobileBanking = () => {
    const [banks, setBanks] = useState([]);
    const [allNumbers, setAllNumbers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingNumber, setEditingNumber] = useState(null);
    const [addForm, setAddForm] = useState({ bank_id: '', number: '', label: '' });

    const numbersByBank = groupNumbersByBank(allNumbers);

    const fetchData = useCallback(() => {
        setLoading(true);
        socketService.emit('fetchMobileBankData'); 
    }, []); 

    useEffect(() => {
        const socket = socketService.getSocket();
        if (!socket || !socket.connected) {
            socketService.connect();
        }

        const handleConnect = () => fetchData();

        const handleMobileBankData = (data) => {
            setLoading(false);
            if (data.error) { setError(data.error); return; }
            setBanks(data.banks || []); 
            setAllNumbers(data.numbers || []);
        };

        const handleConfirmation = (event, data) => {
            if (data.success) {
                fetchData();
            } else {
                alert(`❌ Failed: ${data.message || 'Unknown error'}`);
            }
        };

        socketService.on('connect', handleConnect); 
        socketService.on('mobileBankData', handleMobileBankData);
        socketService.on('MobileBankData-numberAdded', (data) => handleConfirmation('added', data));
        socketService.on('MobileBankData-numberUpdated', (data) => {
             handleConfirmation('updated', data);
             setShowEditModal(false);
             setEditingNumber(null);
        });
        socketService.on('MobileBankData-statusToggled', (data) => handleConfirmation('toggled', data));
        socketService.on('MobileBankData-numberDeleted', (data) => handleConfirmation('deleted', data));
        socketService.on('MobileBankData-statusSetToStop', (data) => handleConfirmation('stopped', data));

        if (socket && socket.connected) {
            fetchData();
        }

        return () => {
            socketService.off('connect', handleConnect); 
            socketService.off('mobileBankData', handleMobileBankData);
            socketService.off('MobileBankData-numberAdded');
            socketService.off('MobileBankData-numberUpdated');
            socketService.off('MobileBankData-statusToggled');
            socketService.off('MobileBankData-numberDeleted');
            socketService.off('MobileBankData-statusSetToStop');
        };
    }, [fetchData]);

    const handleAddSubmit = (e) => {
        e.preventDefault();
        const { bank_id, number, label } = addForm;
        if (!bank_id || !number.trim()) return alert('Provider and number are required');
        socketService.emit('MobileBankData-addNumber', { bank_id, number: number.trim(), label: label.trim() });
        setAddForm({ bank_id: '', number: '', label: '' });
    };

    const handleDelete = (id) => {
        if (window.confirm('Are you sure you want to delete this number?')) {
            socketService.emit('MobileBankData-deleteNumber', { id });
        }
    };

    const handleToggleStatus = (id) => socketService.emit('MobileBankData-toggleStatus', { id });
    const handleStop = (id) => {
        if (window.confirm('Set this number to STOP?')) socketService.emit('MobileBankData-stopNumber', { id });
    };
    
    const openEditModal = (num) => { setEditingNumber(num); setShowEditModal(true); };
    const closeEditModal = () => { setEditingNumber(null); setShowEditModal(false); };

    const handleEditSave = (e) => {
        e.preventDefault();
        socketService.emit('MobileBankData-editNumber', { 
            id: editingNumber.id, 
            number: editingNumber.number.trim(), 
            label: editingNumber.label.trim() 
        });
    };

    return (
        <Layout>
            <div className="main-content-area py-3 px-10">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5 className="section-title m-0">💳 Mobile Banking Management</h5>
                    <div className="count-badge">
                        Total Numbers: <span>{allNumbers.length}</span>
                    </div>
                </div>

                <div className="row g-4">
                    <div className="col-lg-4">
                        <div className="filter-card shadow-sm border-0">
                            <h6 className="fw-bold mb-3 text-primary">Add New Number</h6>
                            <form onSubmit={handleAddSubmit}>
                                <div className="mb-3">
                                    <label className="field-label">SERVICE PROVIDER</label>
                                    <select className="modern-input" value={addForm.bank_id} onChange={(e) => setAddForm({...addForm, bank_id: e.target.value})} required>
                                        <option value="">Choose Provider...</option>
                                        {banks.map((b) => (<option key={b.id} value={b.id}>{b.display_name || b.provider}</option>))}
                                    </select>
                                </div>
                                <div className="mb-3">
                                    <label className="field-label">PHONE NUMBER</label>
                                    <input type="text" className="modern-input" placeholder="01XXXXXXXXX" value={addForm.number} onChange={(e) => setAddForm({...addForm, number: e.target.value})} required />
                                </div>
                                <div className="mb-4">
                                    <label className="field-label">LABEL (OPTIONAL)</label>
                                    <input type="text" className="modern-input" placeholder="Agent, Office etc." value={addForm.label} onChange={(e) => setAddForm({...addForm, label: e.target.value})} />
                                </div>
                                <button type="submit" className="btn btn-apply fw-bold w-100 py-2">Add New Number</button>
                            </form>
                        </div>
                    </div>

                    <div className="col-lg-8">
                        {loading ? (
                            <div className="text-center p-5">Loading...</div>
                        ) : banks.map((b) => (
                            <div className="table-container shadow-sm border-0 mb-4" key={b.id}>
                                <div className="px-4 py-3 bg-white border-bottom d-flex justify-content-between align-items-center">
                                    <h6 className="m-0 fw-bold text-primary">{b.display_name || b.provider}</h6>
                                    <span className="badge bg-light text-dark border">{numbersByBank[b.id]?.length || 0} Numbers</span>
                                </div>
                                <div className="table-responsive">
                                    <table className="custom-zebra-table">
                                        <thead>
                                            <tr>
                                                <th className="ps-4">NUMBER</th>
                                                <th>LABEL</th>
                                                <th className="text-center">STATUS</th>
                                                <th className="text-end pe-4">ACTIONS</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {numbersByBank[b.id]?.length > 0 ? numbersByBank[b.id].map((r) => (
                                                <tr key={r.id}>
                                                    <td className="ps-4">
                                                        <div className="user-primary">{r.number}</div>
                                                    </td>
                                                    <td><div className="sim-num">{r.label || '—'}</div></td>
                                                    <td className="text-center">
                                                        <span className={`badge-status ${
                                                            r.status === 'active' ? 'bg-seen' : 
                                                            r.status === 'inactive' ? 'badge-unseen' : 'bg-fake'
                                                        }`}>
                                                            {r.status}
                                                        </span>
                                                    </td>
                                                    <td className="text-end pe-4">
                                                        <div className="d-flex gap-1 justify-content-end">
                                                            <button className="btn btn-sm btn-light border" title="Edit" onClick={() => openEditModal(r)}>✏️</button>
                                                            <button className="btn btn-sm btn-light border" title="Toggle Status" onClick={() => handleToggleStatus(r.id)}>🔁</button>
                                                            {r.status !== 'stop' && (
                                                                <button className="btn btn-sm btn-light border text-danger" title="Stop Service" onClick={() => handleStop(r.id)}>🛑</button>
                                                            )}
                                                            <button className="btn btn-sm btn-light border text-danger" title="Delete" onClick={() => handleDelete(r.id)}>🗑️</button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )) : (
                                                <tr><td colSpan="4" className="text-center py-4 opacity-50">No numbers added</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* ✅ ফিক্স: মডাল বাটন এবং ডিজাইন আপডেট */}
            {showEditModal && editingNumber && (
                <div className="custom-modal-overlay">
                    <div className="custom-modal-dialog">
                        <div className="custom-modal-content">
                            <div className="modal-header bg-light">
                                <h6 className="m-0 fw-bold">Update Number Details</h6>
                                <button type="button" className="btn-close" onClick={closeEditModal}></button>
                            </div>
                            <form onSubmit={handleEditSave}>
                                <div className="modal-body p-4">
                                    <div className="mb-3">
                                        <label className="field-label">PHONE NUMBER</label>
                                        <input type="text" className="modern-input" value={editingNumber.number} onChange={(e) => setEditingNumber({...editingNumber, number: e.target.value})} required />
                                    </div>
                                    <div className="mb-3">
                                        <label className="field-label">LABEL</label>
                                        <input type="text" className="modern-input" value={editingNumber.label || ''} onChange={(e) => setEditingNumber({...editingNumber, label: e.target.value})} />
                                    </div>
                                </div>
                                {/* 🔥 বাটনগুলো এখন ফুল উইডথ এবং সুন্দর স্পেস পাবে */}
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-reset flex-fill py-2" onClick={closeEditModal}>Cancel</button>
                                    <button type="submit" className="btn btn-apply flex-fill py-2">Save Changes</button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                /* General Styles */
                .main-content-area { background-color: #f4f7fa; min-height: 100vh; font-family: 'Inter', sans-serif; }
                .section-title { color: #334155; font-weight: 700; }
                .count-badge { background: #fff; padding: 5px 15px; border-radius: 50px; border: 1px solid #e2e8f0; font-size: 13px; }
                .count-badge span { color: #0284c7; font-weight: 800; }
                .filter-card { background: #ffffff; padding: 20px; border-radius: 12px; }
                .field-label { font-size: 11px; font-weight: 700; color: #64748b; margin-bottom: 5px; }
                .modern-input { background: #f8fafc; border: 1px solid #cbd5e1; padding: 8px 12px; border-radius: 8px; font-size: 13px; width: 100%; outline: none; transition: 0.2s; }
                .modern-input:focus { border-color: #0284c7; background: #fff; }
                .btn-apply { background: #0284c7; color: white; border-radius: 8px; font-size: 13px; transition: 0.3s; border: none; font-weight: 600; }
                .btn-apply:hover { background: #0369a1; transform: translateY(-1px); }
                .btn-reset { background: #f1f5f9; color: #475569; border: 1px solid #cbd5e1; border-radius: 8px; font-size: 13px; font-weight: 600; }
                
                /* Table Styles */
                .table-container { background: #ffffff; border-radius: 12px; overflow: hidden; }
                .custom-zebra-table { width: 100%; border-collapse: collapse; }
                .custom-zebra-table thead { background: #e2e8f0; color: #475569; }
                .custom-zebra-table th { padding: 14px 15px; font-size: 11px; font-weight: 800; text-transform: uppercase; border-bottom: 2px solid #cbd5e1; text-align: left; }
                .custom-zebra-table tbody tr:nth-child(even) { background-color: #f8fafc; }
                .custom-zebra-table tbody tr:hover { background-color: #edf2f7 !important; }
                .custom-zebra-table td { padding: 12px 15px; vertical-align: middle; border-bottom: 1px solid #edf2f7; }
                .user-primary { color: #0369a1; font-weight: 700; font-size: 14px; }
                .sim-num { color: #1e293b; font-weight: 600; font-size: 13px; }
                .badge-status { padding: 4px 12px; border-radius: 6px; font-size: 11px; font-weight: 700; border: 1px solid transparent; text-transform: capitalize; display: inline-block; }
                .bg-seen { background: #f0fdf4; color: #16a34a; border-color: #bbf7d0; }
                .bg-fake { background: #fee2e2; color: #dc2626; border-color: #fecaca; }
                .badge-unseen { background: #f3f4f6; color: #6b7280; border: 1px solid #e5e7eb; }

                /* 🔥 CUSTOM MODAL STYLES 🔥 */
                .custom-modal-overlay {
                    position: fixed; 
                    top: 0; left: 0; right: 0; bottom: 0;
                    width: 100%; height: 100%;
                    background: rgba(0, 0, 0, 0.7); 
                    z-index: 99999 !important; 
                    display: flex; 
                    justify-content: center;
                    align-items: flex-start; /* Aligns to TOP */
                    padding-top: 60px;       /* Top spacing */
                    overflow-y: auto; 
                }

                .custom-modal-dialog {
                    background: #fff; 
                    width: 90%;            
                    max-width: 550px;      
                    margin: 0 auto 50px auto; 
                    border-radius: 12px; 
                    box-shadow: 0 20px 40px rgba(0,0,0,0.4);
                    position: relative;
                    animation: slideUp 0.3s ease-out;
                    display: flex;
                    flex-direction: column;
                }

                .custom-modal-content {
                    border: none;
                    overflow: hidden;
                    border-radius: 12px;
                    width: 100%;
                }

                .modal-header {
                    padding: 15px 20px; 
                    border-bottom: 1px solid #f1f5f9;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    background: #ffffff;
                    border-radius: 12px 12px 0 0;
                }

                .modal-body {
                    padding: 20px; 
                    overflow-x: hidden; 
                }
                
                /* 🔥 মডাল ফুটার স্টাইল আপডেট */
                .modal-footer {
                    padding: 15px 20px;
                    border-top: none;
                    display: flex;
                    gap: 10px;
                    background: #fff;
                    border-radius: 0 0 12px 12px;
                }

                .btn-close {
                    font-size: 12px;
                    background: transparent url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23000'%3e%3cpath d='M.293.293a1 1 0 0 1 1.414 0L8 6.586 14.293.293a1 1 0 1 1 1.414 1.414L9.414 8l6.293 6.293a1 1 0 0 1-1.414 1.414L8 9.414l-6.293 6.293a1 1 0 0 1-1.414-1.414L6.586 8 .293 1.707a1 1 0 0 1 0-1.414z'/%3e%3c/svg%3e") center/1em auto no-repeat;
                    width: 1em; height: 1em; padding: 0.5em;
                    opacity: 0.5; border: 0; cursor: pointer;
                }
                .btn-close:hover { opacity: 0.8; }

                @keyframes slideUp { 
                    from { transform: translateY(-20px); opacity: 0; } 
                    to { transform: translateY(0); opacity: 1; } 
                }
            `}</style>
        </Layout>
    );
};

export default MobileBanking;